import { apiClient, logHttpFailure } from '@/plugins/http'
import { LOOKUP_REQUEST_CONFIG } from '@/plugins/http/policy'
import { hasAction } from '@/utils/authActions'
import { MENU_ACTION } from '@/utils/routeAccess'
import { MENU_READ, menuReadAttempt, needsMenuRead, sessionMenuOf } from '@/utils/sessionMenu'

const menuModule = {
  namespaced: true,

  state: {
    menuList: null,
    /** 그 목록을 **누구의 세션·어느 언어로** 읽었나(`{ accountId, locale }`) — 계정이 다르면 남의 메뉴다 */
    menuOwner: null,
    /**
     * 아직 목록이 되지 않은 **최근 시도**(`{ accountId, locale, status }` — 읽는 중·실패). 실패를 기억해 이동마다 다시 부르지
     * 않고, 읽는 중이면 또 부르지 않는다. 판정은 `utils/sessionMenu` 한 곳이다(R-L 리뷰 M-1·L-2).
     */
    menuRead: null,
    menuIsCollapse: false,
    isShowMobile: false,
  },

  getters: {
    menuList(state) {
      if (!state.menuList) return null

      const topMenu = state.menuList.filter((item) => item.mlevel === 1)

      state.menuList
        .filter((item) => item.mlevel === 2)
        .forEach((item) => {
          const parent = topMenu.find((menu) => menu.resourcesCd === item.topMenu.resourcesCd)
          if (parent) {
            parent.subMenu = parent.subMenu || []
            parent.subMenu.push(item)
          }
        })

      return topMenu
    },
    menuIsCollapse(state) {
      return state.menuIsCollapse
    },
    currentMenu(state, getters, rootState) {
      if (!state.menuList) return null

      return state.menuList.find((item) => item.url === rootState.route.path) ?? null
    },
    isShowMobile(state) {
      return state.isShowMobile
    },
    /**
     * **이 세션의 메뉴** — 화면 경로 목록이거나 `MENU_STATE`(아직 모른다·못 읽었다)의 하나. 라우터 가드의 증거다
     * (`utils/sessionMenu.sessionMenuOf`).
     */
    sessionMenu(state, getters, rootState, rootGetters) {
      return sessionMenuOf(state, rootGetters['auth/accountId'])
    },
  },

  mutations: {
    setMenuList(state, payload) {
      state.menuList = payload
    },
    setMenuOwner(state, payload) {
      state.menuOwner = payload
    },
    setMenuRead(state, payload) {
      state.menuRead = payload
    },
    setMenuIsCollapse(state, payload) {
      state.menuIsCollapse = payload
    },
    setIsShowMobile(state, payload) {
      state.isShowMobile = payload
    },
  },
  actions: {
    /**
     * 사이드 메뉴 — **부를 수 있을 때만 부른다**. 이 액션이 `GET /mainMenu` 를 부르는 유일한 자리다.
     *
     * 메뉴 집합은 서버의 리소스 표가 정본이라 역할에 따라 0개일 수 있다(운영사·관리점은 이번 회차에
     * 리소스가 0개다 — B2 서버 보고서 §6-8). 그런데 이 앱의 403 은 **화면이 무시할 수 있는 실패가
     * 아니다**: 보안 계층이 거부하면 서버가 그 세션을 끊어서(`InsufficientAuthenticationException`,
     * 서버 보고서 §5 함정 · compose 실측 2026-09-15) 뒤따르는 모든 호출이 401·403 이 된다. 클라이언트
     * 쪽 옵트아웃으로는 되살릴 수 없으므로 **아예 부르지 않는 것**이 유일한 대책이다.
     *
     * 「부를 수 있는가」의 정본은 세션의 `sa_actions` 다 — `MainMenu` 액션이 곧 `GET /mainMenu` 리소스다.
     * 술어는 `utils/authActions.hasAction` 한 곳이다(화면 버튼·라우터 게이트·조회 게이트가 같은 것을 쓴다).
     *
     * 같은 계정·언어로 이미 읽었거나 읽는 중이거나 실패했으면 부르지 않는다(`utils/sessionMenu.needsMenuRead`) — 셸(SideNav)과
     * 보류된 화면(App)이 함께 청해도 요청은 하나이고, 실패한 읽기는 이동마다 되풀이되지 않는다(R-L 리뷰 M-1·L-2).
     *
     * **가벼운 조회 프로필**(`LOOKUP_REQUEST_CONFIG` — 짧은 타임아웃·전역 잠금 로딩 없음·실패 토스트 없음)이다. 이 조회를 기다리는
     * 동안 셸은 이미 그려져 있고 화면 본문만 보류된다(`App.vue`). 실패의 대체 동작은 「막지 않는다」(`MENU_STATE.UNAVAILABLE`)다 —
     * 그래서 **던지지 않는다**: 실패는 기록(값)으로 끝나고 로그 한 줄을 남긴다. 늦게 온 앞 시도의 답은 버린다(언어를 연달아 바꾼 경우).
     */
    async loadMenuList({ commit, state, rootGetters }, payload) {
      const owner = { accountId: rootGetters['auth/accountId'], locale: (payload && payload.locale) || null }
      if (!needsMenuRead(state, owner)) return

      if (!hasAction(rootGetters['auth/auth'], MENU_ACTION)) {
        commit('setMenuList', [])
        commit('setMenuOwner', owner)
        commit('setMenuRead', null)

        return
      }

      const attempt = menuReadAttempt(owner, MENU_READ.LOADING)
      commit('setMenuRead', attempt)

      try {
        const result = await apiClient.get('/mainMenu', Object.assign({}, LOOKUP_REQUEST_CONFIG, { params: payload }))
        if (state.menuRead !== attempt) return

        commit('setMenuList', result.data)
        commit('setMenuOwner', owner)
        commit('setMenuRead', null)
      } catch (error) {
        logHttpFailure(error, 'menu load failed: ')
        if (state.menuRead !== attempt) return

        commit('setMenuRead', menuReadAttempt(owner, MENU_READ.FAILED))
      }
    },
    toggleIsShowMobile({ commit, state }) {
      commit('setIsShowMobile', !state.isShowMobile)
    },
  },
}

export default menuModule
